import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { Icon, type IconName } from "../icons/Icon";
import { AUDIENCE_TEXT, useShows, type Audience } from "../primitives/Audience";
import { cx } from "../utils/format";
import { Menu, type MenuItem } from "../primitives/Menu";
import { PHONE_QUERY, useMedia } from "../utils/media";

export type Navigate = (href: string, e: MouseEvent<HTMLAnchorElement>) => void;

/** an entry's dot: the coloured tone, the quiet grey tone, or none */
export type RailDot = boolean | "quiet";

export interface RailItem { id: string; label: string; /** one word for under the icon, where the label is too long for the rail ("Knowledge") */ short?: string; icon: IconName; href: string;
  /** the prototype's demo count; the app shows `dot` instead (rail dots, 2026-10-18: one meaning for dots, one for numbers) */ badge?: number;
  /** something here waits for you: the kit's one unread dot at the icon's corner, and "<label>, unread" for a screen reader (rail dots, the
   *  founder, 2026-10-18). Two tones (the chat dot round, 2026-11-01): `true` the coloured dot (an unread ticket in your queue, a mention or a
   *  direct message), `"quiet"` the grey one (any other unread message); false or absent draws nothing */ dot?: RailDot; active?: boolean;
  /** who sees the place (the kit's audience rule): `platform` is a placeholder only platform administrators see, with a dashed edge */ audience?: Audience;
  /** the tour's mark on the rail's link (`data-tour`, M11 round three): where a stop points */ tour?: string; }

/** the dashed edge and the words for a screen reader on a place only some viewers see; null for everyone else */
const audienceMark = (audience?: Audience) => (audience === "platform" ? <span className="ob-sr-only">{AUDIENCE_TEXT.platform}</span> : null);

/** The Omnalingo icon-only mark (from the Omnalingo design system), in the current text colour. */
export function Logo() {
  return (
    <svg viewBox="-2 17 104 104" width="34" height="34" aria-hidden="true" fill="var(--ob-text)">
      <path d="M76.6,22.4H22.5c-10.7,0-19.4,8.7-19.4,19.4v73.6h73.6c10.7,0,19.4-8.7,19.4-19.4V41.8C96,31.1,87.3,22.4,76.6,22.4z M49.6,103.5c-20.8,0-35.5-14.7-35.5-34.7c0-20,14.7-34.7,35.5-34.7C70.4,34.2,85,48.9,85,68.9C85,88.9,70.4,103.5,49.6,103.5z" />
      <path d="M49.6,46.3c-13.3,0-22.2,9.5-22.2,22.5s9,22.6,22.2,22.6c13.3,0,22.2-9.6,22.2-22.6S62.8,46.3,49.6,46.3z" />
    </svg>
  );
}

/** what a screen reader hears for an entry: its label, and "unread" while its dot shows ("Inbox, unread"; the founder, 2026-10-18) */
const unreadLabel = (item: RailItem) => (item.dot ? `${item.label}, unread` : item.label);
/** the kit's one unread dot (`.ob-unread-dot`, the inbox row's mark), quiet (`--quiet`, grey) for the quiet tone: drawn, never read aloud
 *  (the entry's label says "unread" for both tones); also on the ticket band's Discussion button (the discussion polish round, 2026-11-02) */
export function UnreadDot({ className, tone }: { className: string; tone: RailDot | undefined }) {
  if (!tone) return null;
  return <span className={cx("ob-unread-dot", tone === "quiet" && "ob-unread-dot--quiet", className)} aria-hidden="true" />;
}

function RailLink({ item, onNavigate }: { item: RailItem; onNavigate: Navigate }) {
  if (!useShows(item.audience)) return null;
  // an icon alone is a guess: every item carries its word, under the icon, always visible (no tooltip needed)
  return (
    <a className={cx("ob-rail__item", item.active && "ob-rail__item--active", item.audience && `ob-rail__item--${item.audience}`)} href={item.href} aria-label={unreadLabel(item)} title={item.audience ? AUDIENCE_TEXT[item.audience] : undefined} data-tour={item.tour} onClick={(e) => onNavigate(item.href, e)}>
      <Icon name={item.icon} />
      <span className="ob-rail__label" aria-hidden="true">{item.short ?? item.label}</span>
      {item.badge != null && item.badge > 0 && <span className="ob-rail__badge">{item.badge > 99 ? "99" : item.badge}</span>}
      <UnreadDot className="ob-rail__dot" tone={item.dot} />
    </a>
  );
}

/** `items` render as one visually grouped block; `before` and `after` render above and below it, each set off by a separator. */
export function Rail({ before = [], items, after = [], bottom, logoHref, brand, tour, onNavigate }: { before?: RailItem[]; items: RailItem[]; after?: RailItem[]; bottom: RailItem[]; logoHref: string; /** the top slot: the workspace's mark and switcher; without it, the product's mark as a link home */ brand?: ReactNode;
  /** the tour's mark on the whole rail (`data-tour`, the founder's icon-bar stop, 2026-10-07) */ tour?: string; onNavigate: Navigate }) {
  return (
    <aside className="ob-rail" aria-label="Primary" data-tour={tour}>
      {brand ? <div className="ob-rail__logo ob-rail__logo--brand">{brand}</div> : <a className="ob-rail__logo" href={logoHref} aria-label="OmnaBase" onClick={(e) => onNavigate(logoHref, e)}><Logo /></a>}
      <nav className="ob-rail__nav">
        {before.map((it) => <RailLink key={it.id} item={it} onNavigate={onNavigate} />)}
        {before.length > 0 && <span className="ob-rail__sep" aria-hidden="true" />}
        <div className="ob-rail__group">{items.map((it) => <RailLink key={it.id} item={it} onNavigate={onNavigate} />)}</div>
        {after.length > 0 && <span className="ob-rail__sep" aria-hidden="true" />}
        {after.map((it) => <RailLink key={it.id} item={it} onNavigate={onNavigate} />)}
      </nav>
      <div className="ob-rail__bottom">{bottom.map((it) => <RailLink key={it.id} item={it} onNavigate={onNavigate} />)}</div>
    </aside>
  );
}

export interface Crumb { /** one quiet line after the current crumb: what this place is for. Cut off when it does not fit, whole in the tooltip; hidden on narrow screens */ note?: string; label: string; href?: string; /** rendered instead of the label, e.g. a project switcher */ node?: ReactNode;
  /** what the back arrow calls this place when it differs from the label: a ticket's crumb reads "Omnalingo #4105", the arrow says "#4105" */ backLabel?: string;
  /** phones only: a quiet number beside the title (a queue's tickets; on a ticket how many are left, this one included) */ phoneCount?: string;
  /** this crumb is what the views column selects (the inbox view, the settings section, the chat channel). As the current crumb on a phone,
   *  where the column is a panel of its own, it is the button that opens that panel: the top bar's `panel` (2026-10-17). Tablets and desktops
   *  show the column beside the content, and the crumb stays plain text there */ views?: boolean; }

/** Where "back" leads: the nearest crumb above the current one that is a link. It is the place, not the browser's history, so it holds after a reload and from a shared link.
 *  None while the current crumb is the views crumb: that is the top of its area, and the bottom bar leads everywhere else. */
export function backCrumb(crumbs: Crumb[]): Crumb | null {
  const last = crumbs[crumbs.length - 1];
  if (!last || last.views) return null;
  return [...crumbs.slice(0, -1)].reverse().find((c) => c.href) ?? null;
}

/** Where the arrow leads. `go` steps back through history instead of following the link; `named` puts the label beside the arrow, for a target that is not simply the crumb above. */
export interface BackTarget { href: string; label: string; named: boolean; go?: () => void; /** only a phone shows it: the way from a list up to its area's first level, which wider screens have beside the list */ phoneOnly?: boolean; }

/** Where the current crumb leads on a phone when it is the `views` crumb: the area's panel (its views column as a screen of its own) */
export interface PanelTarget { /** the area, for the tooltip ("Back to Settings") */ label: string; go: () => void; }

/** The phone title bar's one action at its left end (the mobile polish round, 2026-10-27): an icon button with the label as its spoken name and
 *  tooltip ("New ticket"); with `items` it opens a menu (a sheet on a phone) instead of acting itself. */
export interface PhoneAction { icon: IconName; label: string; onClick?: () => void; items?: MenuItem[]; }

/** the phone action's button: 44 px, the label spoken and in the tooltip; a menu's trigger when it carries items */
function PhoneActionButton({ action }: { action: PhoneAction }) {
  const button = <button type="button" className="ob-topbar__phone-action" aria-label={action.label} title={action.label} onClick={action.items ? undefined : action.onClick}><Icon name={action.icon} /></button>;
  return action.items ? <Menu align="start" trigger={button} sections={[{ items: action.items }]} /> : button;
}

export function Topbar({ crumbs, right, onNavigate, searching, phoneTitle, back, panel, phoneAction }: { crumbs: Crumb[]; right?: ReactNode; onNavigate: Navigate; /** decided by the app (lib/trail); left out, the arrow goes up to the crumb above */ back?: BackTarget | null; /** phones: the search field has taken the bar over */ searching?: boolean;
  /** phones: one action at the bar's left end on an area's first screen ("New ticket"); never beside the back arrow, gone while the search has
   *  the bar, hidden from 768 px up */ phoneAction?: PhoneAction;
  /** phones: what the bar says instead of the crumbs, on the first level of an area ("Inbox", "Settings") */ phoneTitle?: string;
  /** phones: the way to the area's panel; given, a current crumb marked `views` is a button that takes it (what the arrow does on a list) */ panel?: PanelTarget | null }) {
  const up = backCrumb(crumbs);
  const target: BackTarget | null = back !== undefined ? back : up ? { href: up.href!, label: up.label, named: false } : null;
  const follow = (e: MouseEvent<HTMLAnchorElement>) => { if (!target) return; if (target.go && !(e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0)) { e.preventDefault(); target.go(); } else onNavigate(target.href, e); };
  return (
    <header className={cx("ob-topbar", searching && "ob-topbar--search")}>
      {/* one pane and phones: the way up. Wider, the crumbs themselves are the way */}
      {target && <a className={cx("ob-topbar__back", target.named && "ob-topbar__back--named", target.phoneOnly && "ob-topbar__back--phone")} href={target.href} aria-label={`Back to ${target.label}`} onClick={follow}><Icon name="back" />{target.named && <span className="ob-topbar__back-text" aria-hidden="true">{target.label}</span>}</a>}
      {phoneAction && !target && !searching && <PhoneActionButton action={phoneAction} />}
      {phoneTitle && <h1 className="ob-topbar__phonetitle">{phoneTitle}</h1>}
      <nav className={cx("ob-crumbs", phoneTitle && "ob-crumbs--under-title")} aria-label="Breadcrumb">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          const cls = cx("ob-crumbs__item", i === 0 && "ob-crumbs__item--root", last && "ob-crumbs__item--current");
          const plain = c.href && !last ? <a className={cls} href={c.href} onClick={(e) => onNavigate(c.href!, e)}>{c.label}</a> : <span className={cls}>{c.label}</span>;
          return (
            <span key={i} style={{ display: "contents" }}>
              {i > 0 && <Icon name="chevronRight" size="sm" className="ob-crumbs__sep" />}
              {c.node ? <span className={cx(cls, "ob-crumbs__item--node")}>{c.node}</span> : last && c.views && panel ? <PanelCrumb className={cls} label={c.label} panel={panel} /> : plain}
              {last && c.phoneCount && <span className="ob-crumbs__phonecount">{c.phoneCount}</span>}
              {last && c.note && <span className="ob-crumbs__note" title={c.note}>{c.note}</span>}
            </span>
          );
        })}
      </nav>
      <div className="ob-topbar__actions">
        {right}
      </div>
    </header>
  );
}

/** The current crumb that leads to the area's panel: one element, a button on a phone and plain text wider (the flows count exactly one
 *  current crumb). Each form is hidden by the CSS at the other's widths, for the moment between a resize and the render that follows it */
function PanelCrumb({ className, label, panel }: { className: string; label: string; panel: PanelTarget }) {
  const phone = useMedia(PHONE_QUERY);
  return phone
    ? <button type="button" className={cx(className, "ob-crumbs__item--button")} title={`Back to ${panel.label}`} onClick={panel.go}>{label}</button>
    : <span className={cx(className, "ob-crumbs__item--to-panel")}>{label}</span>;
}

/** The small tab that stays when a screen has retracted its views column behind the rail (the ticket page, with a ticket open).
 *  It sits at the level of the rail's active item, on the rail's edge; with the column brought back it rides on the column's far edge.
 *  Should the rail's item not be on screen, it sits at the level of the bands. */
export function ViewsHandle({ retracted, onToggle, what = "queues" }: { retracted: boolean; onToggle: () => void; /** what the column holds, for the label */ what?: string }) {
  const [top, setTop] = useState<number | null>(null);
  useLayoutEffect(() => {
    const place = () => { const r = document.querySelector(".ob-rail__item--active")?.getBoundingClientRect(); setTop(r && r.width > 0 ? Math.round(r.top + r.height / 2 - 22) : null); };
    place();
    const nav = document.querySelector(".ob-rail"); // the rail scrolls as one on a low window: the tab follows its item
    window.addEventListener("resize", place); nav?.addEventListener("scroll", place);
    return () => { window.removeEventListener("resize", place); nav?.removeEventListener("scroll", place); };
  }, []);
  return (
    <button type="button" className={cx("ob-views-handle", !retracted && "ob-views-handle--out")} style={{ top: top ?? "calc(var(--ob-topbar-h) + 6px)" }}
      aria-label={retracted ? `Show ${what}` : `Hide ${what}`} title={retracted ? `Show ${what}` : `Hide ${what}`} aria-expanded={!retracted} onClick={onToggle}>{/* no balloon tooltip: it would set position: relative and make the tab a grid item */}
      <Icon name="chevronRight" size="xs" />
    </button>
  );
}

/** The phone's primary navigation: at most five areas, each an icon with its word; the rest lives behind "More". Shown instead of the rail below 768 px. */
export function TabBar({ items, onNavigate }: { items: RailItem[]; onNavigate: Navigate }) {
  return (
    <nav className="ob-tabbar" aria-label="Primary">
      {items.map((it) => <TabLink key={it.id} item={it} onNavigate={onNavigate} />)}
    </nav>
  );
}

function TabLink({ item: it, onNavigate }: { item: RailItem; onNavigate: Navigate }) {
  if (!useShows(it.audience)) return null;
  return (
    <a className={cx("ob-tabbar__item", it.active && "ob-tabbar__item--active", it.audience && `ob-tabbar__item--${it.audience}`)} href={it.href} aria-label={it.dot ? unreadLabel(it) : undefined} aria-current={it.active ? "page" : undefined} title={it.audience ? AUDIENCE_TEXT[it.audience] : undefined} onClick={(e) => onNavigate(it.href, e)}>
      <Icon name={it.icon} /><span className="ob-tabbar__label">{it.short ?? it.label}</span>
      {it.badge != null && it.badge > 0 && <span className="ob-tabbar__badge">{it.badge > 99 ? "99" : it.badge}</span>}
      <UnreadDot className="ob-tabbar__dot" tone={it.dot} />
      {audienceMark(it.audience)}
    </a>
  );
}

export function AppShell({ rail, tabbar, topbar, banner, bodyClassName, children }: { rail: ReactNode; /** phones: shown instead of the rail */ tabbar?: ReactNode; topbar: ReactNode; /** one line across the page between the top bar and the body (M3: sending is paused); the body keeps its columns under it */ banner?: ReactNode; bodyClassName?: string; children: ReactNode }) {
  return (
    <div className="ob-app">
      {rail}
      <div className="ob-main">
        {topbar}
        {banner ? <div className="ob-app__banner">{banner}</div> : null}
        <div className={cx("ob-body", bodyClassName)}>{children}</div>
      </div>
      {tabbar}
    </div>
  );
}
